import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ShieldCheck, Loader2, Paperclip, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const DOCS = { health: "crm", real_estate: "creci" };
const DOC_LABEL = { cnpj: "CNPJ", crm: "CRM", creci: "CRECI", cpf_mei: "MEI / CPF", outro: "Outro" };
const inp = "mt-1 w-full h-10 px-3 rounded-xl bg-muted border border-border text-sm outline-none focus:ring-2 focus:ring-primary/40";

function ClaimForm({ business, onDone }) {
  const { user } = useAuth();
  const [f, setF] = useState({ name: user?.name || "", role_title: "", phone: "", email: user?.email || "",
    document_type: DOCS[business.business_vertical] || "cnpj", document_number: "", message: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let attachment_id = null;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        attachment_id = (await api.post("/claims/attachment", fd, { headers: { "Content-Type": "multipart/form-data" } })).data.attachment_id;
      }
      await api.post(`/businesses/${business.id}/claim`, { ...f, attachment_id });
      onDone();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Não foi possível enviar o pedido"); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="claim-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="text-xs text-muted-foreground">Seu nome *<input data-testid="claim-name" required value={f.name} onChange={set("name")} className={inp} /></label>
        <label className="text-xs text-muted-foreground">Cargo / função *<input data-testid="claim-role" required value={f.role_title} onChange={set("role_title")} placeholder="Sócio, diretor, médico titular..." className={inp} /></label>
        <label className="text-xs text-muted-foreground">Telefone / WhatsApp *<input data-testid="claim-phone" required value={f.phone} onChange={set("phone")} placeholder="(83) 9 9999-9999" className={inp} /></label>
        <label className="text-xs text-muted-foreground">E-mail *<input data-testid="claim-email" required type="email" value={f.email} onChange={set("email")} className={inp} /></label>
        <label className="text-xs text-muted-foreground">Documento de comprovação *
          <select data-testid="claim-doc-type" value={f.document_type} onChange={set("document_type")} className={inp}>
            {Object.entries(DOC_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </label>
        <label className="text-xs text-muted-foreground">Número do documento *<input data-testid="claim-doc-number" required value={f.document_number} onChange={set("document_number")} className={inp} /></label>
      </div>
      <label className="text-xs text-muted-foreground block">Anexo (opcional) — foto do documento ou cartão CNPJ, JPG/PNG/PDF até 5 MB
        <span className="mt-1 flex items-center gap-2 h-10 px-3 rounded-xl bg-muted border border-dashed border-border text-sm cursor-pointer">
          <Paperclip className="w-4 h-4" />{file ? file.name : "Escolher arquivo"}
          <input data-testid="claim-file" type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
        </span>
      </label>
      <label className="text-xs text-muted-foreground block">Mensagem<textarea data-testid="claim-message" rows={3} value={f.message} onChange={set("message")} className={`${inp} h-auto py-2`} /></label>
      <p className="text-[11px] text-muted-foreground">Seus dados são usados apenas para verificar a propriedade (LGPD) e não ficam públicos.</p>
      <button data-testid="claim-submit" disabled={busy} className="w-full h-11 rounded-full bg-primary text-primary-foreground font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-60">
        {busy && <Loader2 className="w-4 h-4 animate-spin" />} Enviar pedido de reivindicação
      </button>
    </form>
  );
}

export default function ClaimBusiness({ business }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState(false);
  if (!business || business.owner_id) return null;
  const start = () => (user ? setOpen(true) : navigate(`/login?next=${encodeURIComponent(pathname)}`));
  return (
    <>
      <div data-testid="claim-banner" className="rounded-2xl border border-primary/40 bg-primary/5 p-4 flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-semibold flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-primary" /> Este negócio é seu?</p>
          <p className="text-xs text-muted-foreground">Solicite a propriedade para editar informações, fotos e ganhar 30 dias grátis em destaque.</p>
        </div>
        <button data-testid="claim-btn" onClick={start} disabled={sent}
          className="shrink-0 rounded-full bg-primary text-primary-foreground text-sm font-semibold px-4 py-2 active:scale-95 transition-transform whitespace-nowrap disabled:opacity-60">
          {sent ? "Pedido enviado" : "Sou o proprietário"}
        </button>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="claim-dialog">
          <DialogHeader>
            <DialogTitle>Reivindicar {business.name}</DialogTitle>
            <DialogDescription>Nossa equipe confere os dados e responde pelo sino de notificações e por e-mail.</DialogDescription>
          </DialogHeader>
          {sent ? (
            <div className="text-center py-6 space-y-2" data-testid="claim-success">
              <CheckCircle2 className="w-10 h-10 text-success mx-auto" />
              <p className="font-semibold">Pedido enviado!</p>
              <p className="text-sm text-muted-foreground">Você será avisado assim que analisarmos.</p>
            </div>
          ) : <ClaimForm business={business} onDone={() => { setSent(true); toast.success("Pedido de reivindicação enviado."); }} />}
        </DialogContent>
      </Dialog>
    </>
  );
}
